import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { n0, n2 } from "../../lib/format";
import { addDays, iso, monday, monthEnd, monthStart } from "../../lib/dates";
import DataTable from "../../lib/DataTable";
import { Notice } from "../../lib/ui";

/*
 * Daily sales – port of sales_day.php (API: /api/sales/day)
 * Choose a range; each figure is compared with the previous equal period. Fixed comparisons:
 * today vs yesterday, this week vs last week, this month vs last month.
 */

const ranges = () => {
  const t = new Date(), lm = addDays(monthStart(t), -1);
  return {
    today: ["Today", t, t, addDays(t, -1), addDays(t, -1)], yesterday: ["Yesterday", addDays(t, -1), addDays(t, -1), addDays(t, -2), addDays(t, -2)],
    "7days": ["Last 7 days", addDays(t, -6), t, addDays(t, -13), addDays(t, -7)],
    thisweek: ["This week", monday(t), t, addDays(monday(t), -7), addDays(monday(t), -1)],
    thismonth: ["This month", monthStart(t), t, monthStart(lm), monthEnd(lm)],
    "30days": ["Last 30 days", addDays(t, -29), t, addDays(t, -59), addDays(t, -30)],
  };
};
function delta(cur, prv) {
  if (!prv) return { pct: cur > 0 ? 100 : 0, dir: cur > 0 ? "up" : "flat" };
  const pct = Math.round(((cur - prv) / prv) * 1000) / 10;
  return { pct: Math.abs(pct), dir: pct > 0 ? "up" : pct < 0 ? "down" : "flat" };
}
const Chip = ({ cur, prv }) => { const d = delta(cur, prv); return <span className={`delta-chip ${d.dir}`}>{d.dir === "up" ? "↑" : d.dir === "down" ? "↓" : "→"} {d.pct}%</span>; };

export default function SalesDay() {
  const api = useApi();
  const R = useMemo(ranges, []);
  const [range, setRange] = useState("today");
  const [custom, setCustom] = useState(iso(new Date()));
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);

  const q = useMemo(() => {
    if (range === "custom") { const c = new Date(custom + "T00:00:00"); return { from: custom, to: custom, prev_from: iso(addDays(c, -1)), prev_to: iso(addDays(c, -1)) }; }
    const [, a, b, pa, pb] = R[range];
    return { from: iso(a), to: iso(b), prev_from: iso(pa), prev_to: iso(pb) };
  }, [range, custom, R]);
  const load = useCallback(async () => { try { setD(await api.get("/api/sales/day", q)); } catch (e) { setError(e.message); } }, [api, q]);
  useEffect(() => { load(); }, [load]);

  const label = range === "custom" ? new Date(custom + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : R[range][0];
  const cards = [["txn", "Transactions", false], ["qty", "Units sold", false], ["rev", "Revenue", true], ["profit", "Profit", true]];
  const columns = [
    { key: "sale_date", label: "Date" }, { key: "product_name", label: "Product", render: (r) => <b style={{ color: "var(--ink)" }}>{r.product_name}</b> },
    { key: "manufacturer", label: "Brand" }, { key: "product_type", label: "Type" },
    { key: "quantity_sold", label: "Qty", num: true, render: (r) => n0(r.quantity_sold) }, { key: "sale_price", label: "Price", num: true, render: (r) => n2(r.sale_price) },
    { key: "total_amount", label: "Total", num: true, render: (r) => n2(r.total_amount) }, { key: "profit", label: "Profit", num: true, render: (r) => n2(r.profit) },
    { key: "transaction_reference", label: "Reference" },
  ];

  return (
    <main className="pg" style={{ maxWidth: "90rem" }}>
      <PageHeader slug="sales_day" />
      <Notice notice={error && { tone: "err", text: error }} />
      <section className="panel" style={{ marginBottom: "1.25rem" }}>
        <div className="presets" style={{ paddingBottom: ".8rem", alignItems: "center" }}>
          {Object.entries(R).map(([k, [l]]) => <button key={k} type="button" aria-pressed={range === k} onClick={() => setRange(k)}>{l}</button>)}
          <input type="date" aria-label="Pick a day" value={custom} onChange={(e) => { setCustom(e.target.value); setRange("custom"); }} style={{ marginLeft: ".4rem" }} />
        </div>
      </section>
      {d && (
        <>
          <h2 style={{ fontSize: "1.05rem", margin: "0 0 .7rem" }}>{label}</h2>
          <div className="ui-stats">
            {cards.map(([k, l, money]) => (
              <div key={k} className="ui-stat" style={{ display: "block" }}>
                <div className="ui-stat-label">{l}</div>
                <div className="ui-stat-value mono">{n0(d.current[k])}{money && <span className="unit">TZS</span>}</div>
                {d.previous && <div style={{ fontSize: ".75rem", color: "var(--muted)" }}><Chip cur={d.current[k]} prv={d.previous[k]} /> vs {n0(d.previous[k])}</div>}
              </div>
            ))}
          </div>
          <div className="grid2" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(16rem, 1fr))" }}>
            {[["day", "Today vs yesterday"], ["week", "This week vs last week"], ["month", "This month vs last month"]].map(([k, l]) => (
              <section key={k} className="panel card-pad">
                <h3>{l}</h3>
                <dl style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: ".35rem .7rem", margin: 0, fontSize: ".85rem" }}>
                  {[["rev", "Revenue"], ["profit", "Profit"], ["txn", "Transactions"]].map(([m, ml]) => (
                    <div key={m} style={{ display: "contents" }}><dt style={{ color: "var(--muted)" }}>{ml}</dt><dd className="mono" style={{ margin: 0 }}>{n0(d.compare[k][0][m])}</dd>
                      <dd style={{ margin: 0 }}><Chip cur={d.compare[k][0][m]} prv={d.compare[k][1][m]} /></dd></div>))}
                </dl>
              </section>
            ))}
          </div>
          <section className="panel"><DataTable columns={columns} rows={d.rows} rowKey={(r) => r.sale_id} searchPlaceholder="Search sales" initialSort={{ key: "sale_date", dir: -1 }} empty="No sales in this period." /></section>
        </>
      )}
    </main>
  );
}
